import React, { useEffect, useRef } from "react";
import { useKeycloak } from "@react-keycloak/web";
import { useTranslation } from "shared/contexts/I18nContext";

// Displays user avatar and provides a dropdown for account settings, theme, and mode toggles.
const AvatarMenu = ({
  isDropdownOpen,
  toggleDropdown,
  toggleTheme,
  isDarkMode,
  toggleMockMode,
  useMockData,
  userName,
  userRole,
  profilePictureUrl,
  setLocale,
  navigateTo,
}) => {
  const menuRef = useRef(null);
  const { t, locale } = useTranslation();
  const { keycloak } = useKeycloak();

  const getInitials = (name) => {
    if (!name) return "";
    const parts = name.split(" ");
    if (parts.length > 1) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  const avatarContent = profilePictureUrl ? (
    <img
      src={profilePictureUrl}
      alt={userName}
      className="w-10 h-10 rounded-full cursor-pointer"
    />
  ) : (
    <div className="w-10 h-10 rounded-full bg-gray-800 dark:bg-gray-100 flex items-center justify-center text-white dark:text-black font-bold text-sm cursor-pointer">
      {getInitials(userName)}
    </div>
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        isDropdownOpen
      ) {
        toggleDropdown();
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen, toggleDropdown]);

  const handleLogout = () => {
    toggleDropdown();
    if (keycloak?.logout) {
      keycloak.logout({ redirectUri: window.location.origin });
    }
    navigateTo("home");
  };

  return (
    <div className="flex items-center relative" ref={menuRef}>
      <div className="flex flex-col items-end mr-2 text-right">
        <span className="font-medium text-gray-800 dark:text-gray-200 leading-tight">
          {t(userName)}
        </span>
        <span className="text-gray-600 dark:text-gray-400 text-sm leading-tight">
          {t(userRole ?? "Student")}
        </span>
      </div>
      <div onClick={toggleDropdown} className="cursor-pointer">
        {avatarContent}
      </div>
      <div
        className={`absolute top-12 right-0 bg-white dark:bg-gray-700 rounded-lg shadow-lg w-48 z-50 transition-all duration-300 origin-top-right ${isDropdownOpen
          ? "opacity-100 scale-100 block"
          : "opacity-0 scale-95 hidden"
          }`}
      >
        <ul className="p-2 m-0 list-none">
          <li>
            <button
              onClick={() => {
                navigateTo("accountSettings");
                toggleDropdown();
              }}
              className="w-full text-left px-4 py-3 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600"
            >
              {t("accountSettings")}
            </button>

          </li>

          <li>
            <button
              onClick={() => {
                navigateTo("subscriptionPlans");
                toggleDropdown();
              }}
              className="w-full text-left px-4 py-3 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600"
            >
              {t("subscriptionPlans")}
            </button>
          </li>
          <li>
            <button
              onClick={() => {
                toggleTheme();
                toggleDropdown();
              }}
              className="w-full text-left px-4 py-3 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center justify-between"
            >
              {t("theme")}:{" "}
              {isDarkMode ? `☀️ ${t("dark")}` : `🌙 ${t("light")}`}
            </button>
          </li>
          <li>
            <button
              onClick={handleLogout}
              className="w-full text-left px-4 py-3 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600"
            >
              {t("signOut")}
            </button>
          </li>
          {/* <li>
            <button
              onClick={() => {
                toggleMockMode();
                toggleDropdown();
              }}
              className="w-full text-left px-4 py-3 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center justify-between"
            >
              {t("mode")}: {useMockData ? t("mock") : t("live")}
            </button>
          </li> */}
          {/* <li>
            <button
              onClick={() => {
                setLocale(locale === "en" ? "es" : "en");
                toggleDropdown();
              }}
              className="w-full text-left px-4 py-3 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center justify-between"
            >
              {t("language")}: {locale === "en" ? t("english") : t("spanish")}
            </button>
          </li> */}
        </ul>
      </div>
    </div>
  );
};

export default AvatarMenu;
